<template>
  <div class="relative" ref="root">
    <!-- Control: selected chips + text input, styled like a DaisyUI input -->
    <div
      class="flex flex-wrap items-center gap-1 input input-bordered h-auto min-h-10 py-1.5 w-full"
      @click="focusInput"
    >
      <span
        v-for="item in selectedItems"
        :key="item.value"
        class="badge badge-neutral gap-1"
      >
        {{ item.label }}
        <button
          type="button"
          class="cursor-pointer leading-none"
          aria-label="Remove"
          @click.stop="removeValue(item.value)"
        >
          ×
        </button>
      </span>
      <input
        ref="input"
        v-model="query"
        type="text"
        class="grow bg-transparent outline-none border-0 focus:ring-0 min-w-24 p-0"
        :placeholder="modelValue.length ? '' : placeholder"
        @focus="open = true"
        @keydown.down.prevent="move(1)"
        @keydown.up.prevent="move(-1)"
        @keydown.enter.prevent="onEnter"
        @keydown.delete="onBackspace"
        @keydown.esc="open = false"
      />
    </div>

    <!-- Dropdown -->
    <ul
      v-if="open && (filteredOptions.length || (taggable && query.trim()))"
      class="absolute z-30 mt-1 w-full max-h-60 overflow-y-auto rounded-box border border-base-300 bg-base-200 shadow-lg py-1"
    >
      <li
        v-for="(opt, i) in filteredOptions"
        :key="opt.value"
        class="px-3 py-1.5 cursor-pointer text-base-content hover:bg-base-300"
        :class="{ 'bg-base-300': i === highlighted }"
        @mousedown.prevent="addValue(opt.value)"
        @mouseenter="highlighted = i"
      >
        {{ opt.label }}
      </li>
      <li
        v-if="taggable && query.trim() && !exactMatch"
        class="px-3 py-1.5 cursor-pointer text-base-content hover:bg-base-300"
        :class="{ 'bg-base-300': highlighted === filteredOptions.length }"
        @mousedown.prevent="addValue(query.trim())"
        @mouseenter="highlighted = filteredOptions.length"
      >
        Create "{{ query.trim() }}"
      </li>
    </ul>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from "vue";

interface Option {
  label: string;
  value: string;
}

const props = withDefaults(
  defineProps<{
    modelValue: string[];
    options: Option[];
    taggable?: boolean;
    placeholder?: string;
  }>(),
  { taggable: false, placeholder: "Search…" }
);

const emit = defineEmits<{ "update:modelValue": [string[]] }>();

const root = ref<HTMLElement | null>(null);
const input = ref<HTMLInputElement | null>(null);
const query = ref("");
const open = ref(false);
const highlighted = ref(0);

// Resolve each selected value to a label (fall back to the raw value for ad-hoc tags).
const selectedItems = computed<Option[]>(() =>
  props.modelValue.map(
    (v) => props.options.find((o) => o.value === v) || { label: v, value: v }
  )
);

const filteredOptions = computed(() => {
  const q = query.value.trim().toLowerCase();
  return props.options.filter(
    (o) =>
      !props.modelValue.includes(o.value) &&
      (!q || o.label.toLowerCase().includes(q))
  );
});

const exactMatch = computed(() => {
  const q = query.value.trim().toLowerCase();
  return props.options.some((o) => o.label.toLowerCase() === q || o.value.toLowerCase() === q);
});

function focusInput() {
  input.value?.focus();
}

function addValue(value: string) {
  if (!value || props.modelValue.includes(value)) {
    query.value = "";
    return;
  }
  emit("update:modelValue", [...props.modelValue, value]);
  query.value = "";
  highlighted.value = 0;
}

function removeValue(value: string) {
  emit(
    "update:modelValue",
    props.modelValue.filter((v) => v !== value)
  );
}

function move(delta: number) {
  open.value = true;
  const max = filteredOptions.value.length - (props.taggable && query.value.trim() && !exactMatch.value ? 0 : 1);
  highlighted.value = Math.min(Math.max(highlighted.value + delta, 0), Math.max(max, 0));
}

function onEnter() {
  if (highlighted.value < filteredOptions.value.length) {
    addValue(filteredOptions.value[highlighted.value].value);
  } else if (props.taggable && query.value.trim()) {
    addValue(query.value.trim());
  }
}

function onBackspace() {
  if (query.value === "" && props.modelValue.length) {
    removeValue(props.modelValue[props.modelValue.length - 1]);
  }
}

function onClickOutside(e: MouseEvent) {
  if (root.value && !root.value.contains(e.target as Node)) {
    open.value = false;
  }
}

onMounted(() => document.addEventListener("click", onClickOutside));
onBeforeUnmount(() => document.removeEventListener("click", onClickOutside));
</script>
